// Site devices grouped floor → area. Read-only; devices are created via
// the layout-import wizard. device_class_config arrives redacted from the
// backend, so the JSON view never shows tuya_local_key.

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { devices as devicesApi } from "../../api/endpoints/devices";
import { adminQueryOptions } from "../../api/adminQueryOptions";
import { qk } from "../../api/queryKeys";
import { EmptyState } from "../../components/ui/EmptyState";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { Pill } from "../../components/ui/Pill";
import { Table } from "../../components/ui/Table";
import type { DeviceOut } from "../../api/types/device";

import { DeviceEditModal } from "./DeviceEditModal";

interface DeviceListPanelProps {
  accountSlug: string;
  tenantSlug: string;
  siteSlug: string;
}

interface AreaGroup {
  areaSlug: string;
  areaLabel: string;
  devices: DeviceOut[];
}
interface FloorGroup {
  floorSlug: string;
  floorLabel: string;
  areas: AreaGroup[];
}

function group(rows: DeviceOut[]): FloorGroup[] {
  const floors = new Map<string, FloorGroup>();
  for (const d of rows) {
    let f = floors.get(d.floor_slug);
    if (!f) {
      f = { floorSlug: d.floor_slug, floorLabel: d.floor_label, areas: [] };
      floors.set(d.floor_slug, f);
    }
    let a = f.areas.find((x) => x.areaSlug === d.area_slug);
    if (!a) {
      a = { areaSlug: d.area_slug, areaLabel: d.area_label, devices: [] };
      f.areas.push(a);
    }
    a.devices.push(d);
  }
  return [...floors.values()];
}

export function DeviceListPanel({
  accountSlug,
  tenantSlug,
  siteSlug,
}: DeviceListPanelProps) {
  const q = useQuery({
    ...adminQueryOptions,
    queryKey: qk.devices(accountSlug, tenantSlug, siteSlug),
    queryFn: () =>
      devicesApi.list(accountSlug, tenantSlug, siteSlug).then((r) => r.data),
  });

  const [editing, setEditing] = useState<DeviceOut | null>(null);
  const groups = useMemo(() => group(q.data ?? []), [q.data]);

  if (q.isPending) return <p style={{ color: "var(--text-dim)" }}>Loading…</p>;
  if (q.error)
    return <ErrorBanner error={q.error} title="Failed to load devices" />;
  if (groups.length === 0)
    return (
      <EmptyState
        title="No devices yet"
        description="Use “Import layout” above to upload a CSV of this site's floors, areas, and devices."
      />
    );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {groups.map((f) => (
        <section key={f.floorSlug}>
          <h3 style={{ fontSize: "0.95rem", margin: "0 0 8px" }}>
            {f.floorLabel} <code style={{ fontSize: "0.75rem", color: "var(--text-dim)" }}>{f.floorSlug}</code>
          </h3>
          {f.areas.map((a) => (
            <div key={a.areaSlug} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: "0.82rem", color: "var(--text-dim)", marginBottom: 4 }}>
                {a.areaLabel} · <code>{a.areaSlug}</code> · {a.devices.length} device(s)
              </div>
              <Table<DeviceOut>
                columns={[
                  {
                    key: "display_label",
                    header: "Device",
                    render: (d) => d.display_label,
                  },
                  {
                    key: "device_class",
                    header: "Class",
                    render: (d) => <Pill tone="info">{d.device_class}</Pill>,
                  },
                  {
                    key: "ha_entity_id_stem",
                    header: "HA entity stem",
                    render: (d) => (
                      <code style={{ fontSize: "0.76rem" }}>{d.ha_entity_id_stem}</code>
                    ),
                  },
                  {
                    key: "slug",
                    header: "Canonical slug",
                    render: (d) => (
                      <code style={{ fontSize: "0.76rem", color: "var(--text-dim)" }}>
                        {d.slug}
                      </code>
                    ),
                  },
                  {
                    key: "edit",
                    header: "",
                    align: "right",
                    render: (d) => (
                      <button type="button" style={editBtn} onClick={() => setEditing(d)}>
                        Edit
                      </button>
                    ),
                  },
                ]}
                rows={a.devices}
                rowKey={(d) => d.id}
              />
            </div>
          ))}
        </section>
      ))}

      <DeviceEditModal
        accountSlug={accountSlug}
        tenantSlug={tenantSlug}
        siteSlug={siteSlug}
        device={editing}
        onClose={() => setEditing(null)}
      />
    </div>
  );
}

const editBtn: React.CSSProperties = {
  background: "transparent",
  color: "var(--accent)",
  border: "1px solid var(--border)",
  padding: "2px 10px",
  borderRadius: 4,
  fontSize: "0.78rem",
  cursor: "pointer",
};
